// classifyScan — three-tier traffic-light classifier for trip_scans.
//
//   green   the scan resolves to a canonical_id
//   yellow  we know its name (OFF productName, prior correction productName,
//           or — after pair-anchoring — the list item's typed name) but
//           don't have a canonical bound yet
//   red     no name at all → caller must prompt the user inline so we
//           never persist a bare UPC as someone's pantry identity
//
// The classifier is PURE: it composes the existing identity helpers
// (findBarcodeCorrection / lookupBarcode / inferCanonicalFromName)
// upstream, then derives the tier from the merged result. No I/O lives
// in here so it's trivially testable and the caller controls when each
// lookup runs.
//
// trip_scans.status was created in migration 0126 as the same enum
// (green | yellow | red); the classifier output drops directly into
// the status column.

import { findIngredient, inferCanonicalFromName } from "../data/ingredients";
import { stripFlavors } from "./stripFlavors";
import { parsePackageSize } from "./canonicalResolver";

// Brand sentinels for categories where most products genuinely lack a
// brand on the package — fresh produce sold loose, fresh seafood at the
// counter. Without these, the GREEN tier rule (canonical && brand &&
// hasSize) was unreachable for those categories. The sentinel is also
// exported so display composition (buildDisplayName) can skip it
// instead of reading "Fresh Produce Kale" in the kitchen card title.
export const BRAND_SENTINELS = {
  produce: "Fresh Produce",
  seafood: "Fresh Seafood",
};
export const BRAND_SENTINEL_VALUES = new Set(Object.values(BRAND_SENTINELS));
export function isBrandSentinel(brand) {
  return typeof brand === "string" && BRAND_SENTINEL_VALUES.has(brand);
}
function brandSentinelForCategory(category) {
  return BRAND_SENTINELS[category] || null;
}

// Public: classify a merged identity payload into a tier + display name.
// Inputs are pre-fetched by the caller (so the caller can run the OFF
// lookup and correction lookup in parallel and pass the merged shape).
//
//   correction  — { canonicalId?, productName?, brand?, ... } | null
//                 from findBarcodeCorrection — wins over OFF
//   off         — { found, productName?, brand?, canonicalId?, ... } | null
//                 from lookupBarcode
//   armedListItem — { canonicalId?, name? } | null
//                 the shopping_list_items row the user armed before
//                 scanning (pair-anchoring rule)
//
// Returns:
//   { tier, canonicalId, brand, productName, displayName, sources }
//
// `displayName` is the human-readable string that EVERY UI surface must
// render. The list never falls back to the UPC.
export function classifyScan({ upc, correction, off, armedListItem } = {}) {
  const sources = {};

  // 1. Resolve canonical (any tier wins; cascade: correction → OFF →
  //    name-infer on OFF productName → name-infer on OFF genericName →
  //    pair-anchor → null). genericName is OFF's "Greek yogurt" /
  //    "potato chips" type field — usually CLEANER than productName for
  //    inference because it lacks brand/flavor noise. We try it as a
  //    fallback so a row with productName="Chobani Zero Vanilla Greek
  //    Yogurt 5.3oz" that fails composition still binds via
  //    genericName="Greek yogurt".
  let canonicalId = null;
  if (correction?.canonicalId) {
    canonicalId = correction.canonicalId;
    sources.canonicalId = "correction";
  } else if (off?.canonicalId) {
    canonicalId = off.canonicalId;
    sources.canonicalId = "off";
  } else if (off?.productName) {
    const inferred = inferCanonicalFromName(off.productName);
    if (inferred) {
      canonicalId = inferred;
      sources.canonicalId = "name-infer";
    }
  }
  if (!canonicalId && off?.genericName) {
    const inferred = inferCanonicalFromName(off.genericName);
    if (inferred) {
      canonicalId = inferred;
      sources.canonicalId = "generic-name-infer";
    }
  }

  // 2. Pair-anchoring — the user already declared "I need nutmeg" by
  //    arming a list item with a canonical. Inherit it whenever we
  //    couldn't bind from OFF/correction. Promotes yellow→green and
  //    red→green for paired scans.
  if (!canonicalId && armedListItem?.canonicalId) {
    canonicalId = armedListItem.canonicalId;
    sources.canonicalId = "pair-anchor";
  }

  // 3. Resolve display name. Order:
  //    canonical > correction.productName > OFF productName (stripped
  //    of flavors per CLAUDE.md OFF rules) > armed list item name >
  //    null. Bare UPC is never a fallback — that's the whole point of
  //    the red tier.
  let displayName = null;
  let productName = null;

  if (canonicalId) {
    const ing = findIngredient(canonicalId);
    displayName = ing?.name || null;
    sources.displayName = sources.canonicalId;
  }
  if (!displayName && correction?.productName) {
    productName = correction.productName;
    displayName = productName;
    sources.displayName = "correction";
  }
  // Pre-compute the strip even when we already have a displayName
  // from canonical/correction — we still want to extract claims
  // ("Spicy", "Sharp", "Smoked") from OFF productName so they
  // survive on the row instead of getting silently dropped.
  const offStripped = off?.productName
    ? stripFlavors(off.productName, { brand: off.brand || null, removeFromName: true })
    : null;

  if (!displayName && off?.productName) {
    // OFF productName is INPUT for resolution but never a canonical
    // identity (CLAUDE.md OFF productName ban). It can stand in as a
    // human-readable label, but we strip flavor/variant tokens so the
    // displayed string is identity-shaped, not marketing copy.
    productName = offStripped?.name?.trim() || off.productName;
    displayName = productName;
    sources.displayName = "off";
  }
  if (!displayName && armedListItem?.name) {
    displayName = armedListItem.name;
    sources.displayName = "list-item";
  }

  // 4. Brand — independent of the canonical decision; correction wins
  //    over OFF. When brand is genuinely absent AND the canonical's
  //    category is one where most products don't carry a brand on the
  //    package (loose produce sold by weight, fresh seafood at the
  //    counter), pin a sentinel so the GREEN tier rule isn't an
  //    impossible bar. Real brands type-overrides at any time.
  let brand =
    correction?.brand?.trim() ||
    off?.brand?.trim() ||
    null;
  if (!brand && canonicalId) {
    const cIng = findIngredient(canonicalId);
    const sentinel = brandSentinelForCategory(cIng?.category);
    if (sentinel) {
      brand = sentinel;
      sources.brand = "category-sentinel";
    }
  }

  // 5. Package size — parsed from OFF.quantity (e.g. "2 oz", "200 g").
  //    HARD REQUIREMENT for GREEN, no exceptions. The system needs the
  //    bought amount to compute calories per row and to subtract from
  //    pantry stock when a recipe consumes the ingredient. Loose
  //    produce / seafood sold by weight still has a size — the user
  //    weighed it at the counter and the receipt has a number — so
  //    the row stays YELLOW until the user types it in. DO NOT add
  //    sentinel-style waivers here.
  const packageSize = off?.quantity ? parsePackageSize(off.quantity) : null;
  const hasSize = !!(packageSize?.amount && packageSize?.unit);

  // 6. Tier rules (per user, 2026-05-03):
  //    GREEN  — fully resolved: canonical AND brand AND package size.
  //             Anything less is not "fully resolved" and shouldn't
  //             read as such in the cart.
  //    YELLOW — we have at least a name (canonical OR off productName).
  //             User will resolve the rest in the Resolve phase.
  //    RED    — no name at all → inline quick-name prompt.
  let tier;
  if (canonicalId && brand && hasSize) {
    tier = "green";
  } else if (displayName || canonicalId) {
    tier = "yellow";
  } else {
    tier = "red";
  }

  // Claims — modifier / variant tokens we extract from every signal
  // OFF gave us, not just the stripped productName. Three sources are
  // merged here:
  //
  //   a. stripFlavors(productName) — flavor descriptors like "Spicy",
  //      "Sour Cream & Onion", "Honey BBQ" pulled from marketing copy.
  //   b. labelTags — OFF's structured certification / dietary axis:
  //      "usda-organic", "kosher", "halal", "vegan", "gluten-free",
  //      "non-gmo", "fair-trade", etc. These are CLAIMS by definition;
  //      previously we requested them from the edge function and then
  //      dropped them on the floor.
  //   c. originTags — single most-specific origin claim ("italy",
  //      "france") when present. Origin is a real variant axis for
  //      cheeses, oils, vinegars, cured meats. Capped at one to avoid
  //      noisy "italy, france, european-union" stacks.
  //
  // Lowercased + trimmed + deduped for stable storage. Order roughly
  // reflects priority — flavors first (most semantically loaded),
  // labels next, origin last.
  const rawClaims = [];
  if (Array.isArray(offStripped?.claims)) rawClaims.push(...offStripped.claims);
  if (Array.isArray(off?.labelTags)) rawClaims.push(...off.labelTags);
  if (Array.isArray(off?.originTags) && off.originTags.length > 0) {
    rawClaims.push(off.originTags[0]);
  }
  const claims = [];
  const seen = new Set();
  for (const c of rawClaims) {
    const t = typeof c === "string" ? c.trim().toLowerCase() : "";
    if (!t || seen.has(t)) continue;
    seen.add(t);
    claims.push(t);
  }

  return {
    tier,
    canonicalId,
    brand,
    productName: productName || null,
    displayName,
    packageSize,
    claims,
    sources,
  };
}

// reclassifyScanRow — given a current trip_scans row shape (from the
// store) plus an optional armedListItem, recompute the tier. Used
// after the user manually edits axes in ResolveScreen so the tier
// flips live.
//
// IMPORTANT: passes the persisted off_payload along so the package-
// size check in classifyScan can re-evaluate without an OFF round-
// trip. Without this, every reclassify would lose package-size info
// and the tier would never flip back to GREEN even after the user
// adds a brand.
export function reclassifyScanRow(scan, armedListItem = null) {
  return classifyScan({
    upc: scan.barcodeUpc,
    correction: {
      canonicalId: scan.canonicalId || null,
      productName: scan.productName || null,
      brand: scan.brand || null,
    },
    off: scan.offPayload || null,
    armedListItem,
  });
}
